import type { TFunction } from 'i18next'
import {
  isParked,
  type Recurrence,
  type ReportPeriod,
  type ReportSchedule,
  type WeekDay,
} from '@/services/reports/reportsApi'
import { formatDateTime } from '@/utils/format'

/**
 * How a schedule runs, as a handful of choices people actually make — rather
 * than the recurrence × period × day × time matrix the API takes, most of whose
 * combinations mean nothing.
 *
 * Each preset is one sentence's worth of intent ("every Monday, covering last
 * week"); anything outside them is still reachable through the custom fields,
 * and a schedule made elsewhere is recognised by {@link cadenceOf}.
 */
export interface Cadence {
  key: CadenceKey
  labelKey: string
  /** One line saying what lands in the inbox and what it covers. */
  summaryKey: string
  recurrence: Recurrence
  period: ReportPeriod
  periodTime?: string
  periodDayOfWeek?: WeekDay
  periodDayOfMonth?: number
  /** Presets that report over a flight need a report type that has one. */
  needsFlight?: boolean
}

export type CadenceKey =
  | 'dailyYesterday'
  | 'dailyToDate'
  | 'weekly'
  | 'monthly'
  | 'endOfFlight'
  | 'once'

/** Morning delivery: the report is waiting when the day starts. */
const MORNING = '07:00'

export const CADENCES: Cadence[] = [
  {
    key: 'dailyYesterday',
    labelKey: 'reports.cadence.dailyYesterday',
    summaryKey: 'reports.cadence.dailyYesterdaySummary',
    recurrence: 'DAILY',
    period: 'RECURRENCE_PERIOD',
    periodTime: MORNING,
  },
  {
    key: 'dailyToDate',
    labelKey: 'reports.cadence.dailyToDate',
    summaryKey: 'reports.cadence.dailyToDateSummary',
    recurrence: 'DAILY',
    period: 'FLIGHT',
    periodTime: MORNING,
    needsFlight: true,
  },
  {
    key: 'weekly',
    labelKey: 'reports.cadence.weekly',
    summaryKey: 'reports.cadence.weeklySummary',
    recurrence: 'WEEKLY',
    period: 'RECURRENCE_PERIOD',
    periodTime: MORNING,
    periodDayOfWeek: 'MONDAY',
  },
  {
    key: 'monthly',
    labelKey: 'reports.cadence.monthly',
    summaryKey: 'reports.cadence.monthlySummary',
    recurrence: 'MONTHLY',
    period: 'RECURRENCE_PERIOD',
    periodTime: MORNING,
    periodDayOfMonth: 1,
  },
  {
    key: 'endOfFlight',
    labelKey: 'reports.cadence.endOfFlight',
    summaryKey: 'reports.cadence.endOfFlightSummary',
    recurrence: 'END_OF_FLIGHT',
    period: 'FLIGHT',
    needsFlight: true,
  },
  {
    key: 'once',
    labelKey: 'reports.cadence.once',
    summaryKey: 'reports.cadence.onceSummary',
    recurrence: 'ONCE',
    period: 'FLIGHT',
    needsFlight: true,
  },
]

/**
 * Only line item reports can run at the end of a flight, and only report types
 * with a flight can report over one — the API rejects the rest, so they are
 * never offered.
 */
export function cadencesFor(reportType: string | undefined): Cadence[] {
  const hasFlight = ['LINE_ITEM', 'LINE_ITEM_LIST', 'ADVERTISER', 'SALESPERSON', 'SELF_SERVICE'].includes(
    reportType ?? ''
  )
  const endOfFlight = ['LINE_ITEM', 'SELF_SERVICE'].includes(reportType ?? '')
  return CADENCES.filter((cadence) => {
    if (cadence.key === 'endOfFlight') return endOfFlight
    return !cadence.needsFlight || hasFlight
  })
}

export function cadence(key: CadenceKey): Cadence {
  return CADENCES.find((c) => c.key === key) ?? CADENCES[0]
}

/** The preset a saved schedule matches, or null when it was built by hand. */
export function cadenceOf(schedule: ReportSchedule): Cadence | null {
  return (
    CADENCES.find(
      (c) =>
        c.recurrence === schedule.recurrence &&
        c.period === schedule.period &&
        (c.periodDayOfWeek ?? null) === schedule.periodDayOfWeek &&
        (c.periodDayOfMonth ?? null) === schedule.periodDayOfMonth &&
        (c.periodTime ?? '') === schedule.periodTime
    ) ?? null
  )
}

/** The days a weekly schedule can run on, in the order a week is read. */
export const WEEK_DAYS: WeekDay[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
]

/** A monthly schedule can only land on a day every month has. */
export const MAX_DAY_OF_MONTH = 28

/** When a schedule next runs, or why it is not going to. */
export function nextRunText(schedule: ReportSchedule, t: TFunction): string {
  if (schedule.objectState !== 'ACTIVE') return t('reports.pausedState')
  if (schedule.warnings.length > 0) return schedule.warnings[0]
  if (!schedule.scheduledFor) return t('reports.beingScheduled')
  if (isParked(schedule)) return t('reports.willNotRunAgain')
  return t('reports.nextRun', { when: formatDateTime(schedule.scheduledFor) })
}

/** The cadence a schedule matches, or its bare recurrence when it was built by hand. */
export function cadenceText(schedule: ReportSchedule, t: TFunction): string {
  const matched = cadenceOf(schedule)
  if (matched) return t(matched.labelKey)
  return schedule.recurrence ? t(`reports.recurrence.${schedule.recurrence}`) : t('reports.custom')
}
